import { SelectBox } from '@components/primitives/SelectBox/SelectBox.client'
import { classMerge } from '@components/utilities/classMerge'
import isNotNull from '@lib/utilities/object/isNotNull'
import { useEffect, useMemo, useState } from 'react'
import Input from '../Input'
import { Months } from './Months'

export const DatePicker = ({
  className,
  id,
  initialValue,
  maxMenuHeight,
  maxYear = new Date().getFullYear(),
  minYear = new Date().getFullYear() - 100,
  onChange,
  required,
}: {
  className?: string
  id: string
  initialValue?: string
  maxMenuHeight?: number
  maxYear?: number
  minYear?: number
  onChange: (value: string) => void
  required?: boolean
}) => {
  const initialDate = initialValue ? new Date(initialValue) : undefined

  const initialMonthZeroIndex = initialDate?.getUTCMonth()
  const [selectedMonth, setMonth] = useState<string | undefined>(
    isNotNull(initialMonthZeroIndex)
      ? initialMonthZeroIndex.toString()
      : undefined,
  )
  const [selectedDay, setDay] = useState<number | undefined>(
    initialDate ? initialDate.getUTCDate() : undefined,
  )
  const [selectedYear, setYear] = useState<number | undefined>(
    initialDate ? initialDate.getUTCFullYear() : undefined,
  )

  useEffect(() => {
    if ((selectedDay ?? 1) > daysInMonth) {
      setDay(daysInMonth - 1)
    }
    if (selectedMonth && selectedDay && selectedYear) {
      const ISOStringDate = new Date(
        Date.UTC(selectedYear, parseInt(selectedMonth, 10), selectedDay),
      ).toISOString()
      onChange(ISOStringDate)
    }
  }, [selectedMonth, selectedDay, selectedYear])

  const monthOptions = Months.en.map((month, index) => {
    return { label: month, value: `${index}` }
  })

  const daysInMonth = useMemo(
    () =>
      selectedMonth
        ? new Date(
            selectedYear ?? 2019,
            parseInt(selectedMonth, 10) + 1, // months are zero indexed
            0,
          ).getDate() + 1
        : 31,
    [selectedMonth, selectedYear],
  )

  return (
    <>
      {/* make sure that these swallow enter */}
      <div
        className='flex flex-row gap-2 align-middle'
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.stopPropagation()
          }
        }}
      >
        <SelectBox
          autoFocus={isNotNull(selectedMonth) ? false : true} // autofocus if no month selected
          className={classMerge(
            'h-14 w-1/2 items-center rounded-md border-2',
            className,
          )}
          instanceId={`${id}-month-select`}
          isClearable={false}
          items={monthOptions}
          maxMenuHeight={maxMenuHeight}
          onChange={(val) => {
            setMonth(val)
            document.getElementById(`${id}-day-input`)?.focus()
          }}
          placeholder='Month'
          required={required}
          showBorderStates={true}
          value={selectedMonth}
        />
        <Input
          className={classMerge(
            'h-14 w-[20%] rounded-md border-2 bg-white px-3 text-lg',
            className,
          )}
          error={
            !!selectedDay && (selectedDay < 1 || selectedDay > daysInMonth - 1)
          }
          id={`${id}-day-input`}
          inputMode='numeric'
          max={daysInMonth - 1}
          min={1}
          name='day'
          onBlur={() => {
            if (selectedDay) {
              document.getElementById(`${id}-year-input`)?.focus()
            }
          }}
          onChange={(event) => {
            if (!event.target.value) {
              return setDay(undefined)
            }

            if (event.target.value.length > 2) {
              // don't let the user type in more than 2 digits
              return
            }

            const day = parseInt(event.target.value, 10)
            if (day < 1) {
              return setDay(1)
            }
            if (day > daysInMonth - 1) {
              return setDay(daysInMonth - 1)
            }
            return setDay(day)
          }}
          pattern='[0-9]*'
          placeholder='Day'
          required={required}
          type='number'
          value={selectedDay}
        />
        <Input
          className={classMerge(
            'h-14 w-[30%] rounded-md border-2 bg-white px-3 text-lg',
            className,
          )}
          error={
            !!selectedYear && (selectedYear < minYear || selectedYear > maxYear)
          }
          id={`${id}-year-input`}
          inputMode='numeric'
          max={maxYear}
          min={minYear}
          name='year'
          onChange={(event) => {
            if (!event.target.value) {
              return setYear(undefined)
            }
            if (event.target.value.length > 4) {
              // don't let the user type in more than 4 digits
              return
            }
            const year = parseInt(event.target.value, 10)
            if (year < 0) {
              return setYear(minYear)
            }
            if (year > maxYear) {
              return setYear(maxYear)
            }
            return setYear(year)
          }}
          pattern='[0-9]*'
          placeholder='Year'
          required={required}
          type='number'
          value={selectedYear}
        />
      </div>
    </>
  )
}
